<template>
  <div
    class="main-product critical-component-clear"
    :class="{
      [$class('loading')]: state === 'loading',
      'main-product--gift-card': isGiftCard
    }"
  >
    <div
      v-if="isMobile"
      class="main-product__breadcrumbs-wrapper"
    >
      <breadcrumbs
        :breadcrumb-list="liquid.breadcrumbs?.breadcrumbList"
        :product-details="{
          title: (product || liquid).title,
          url: (product || liquid).url
        }"
        page="product"
      />
    </div>

    <div class="grid">
      <div
        id="product-gallery"
        class="main-product__gallery-container col xs-span l1-33"
        data-sign-post-location="main"
        js-sign-post
        :class="{
          [$class('hidden')]: !isGalleryVisible && !mobileHorizontalScroll,
          'is-vertical-gallery': !mobileHorizontalScroll,
        }"
      >
        <product-gallery
          v-if="!mobileHorizontalScroll"
          class="main-product__gallery"
          :active-group-name="activeMerge?.swatch?.name"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          layout="vertical"
          :loading="state === 'loading'"
          :product="product || liquid"
          :hide-gallery-video="hideGalleryVideo"
          :stl-classes="shopTheLookClasses"
          :show-outfit-anchor="hasOutfitKeys"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
          @update-is-video="handleUpdateIsVideo"
        />

        <product-gallery
          v-if="mobileHorizontalScroll"
          class="main-product__gallery"
          :active-group-name="activeMerge?.swatch?.name"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          layout="vertical"
          :loading="state === 'loading'"
          :product="product || liquid"
          is-mobile-horizontal-scroll
          :show-mobile-arrows="mobileShowGalleryArrows"
          :stl-classes="shopTheLookClasses"
          :show-outfit-anchor="hasOutfitKeys"
          :hide-gallery-video="hideGalleryVideo"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
          @update-is-video="handleUpdateIsVideo"
        />
      </div>

      <div
        v-if="!mobileHorizontalScroll"
        class="main-product__gallery-spacer col xs-span"
      />

      <div
        id="product-details"
        class="main-product__details-wrapper col xs-span l37-59"
      >
        <div
          ref="stickyDetails"
        >
          <div class="main-product__details-placement">
            <div
              class="main-product__details-content"
              :class="{ 'main-product__details-content--outfits': hasOutfitKeys }"
            >
              <div
                class="main-product__details-header"
              >
                <div
                  v-if="!isMobile"
                  class="main-product__breadcrumbs-wrapper"
                >
                  <breadcrumbs
                    :breadcrumb-list="liquid.breadcrumbs?.breadcrumbList"
                    :product-details="{
                      title: (product || liquid).title,
                      url: (product || liquid).url
                    }"
                    page="product"
                  />
                </div>
                <div
                  v-if="hasTopBadges"
                  class="main-product__badges"
                >
                  <product-badge
                    :product="product || liquid"
                    badge-class=" main-product__badge"
                    badge-element="span"
                    badge-type="secondaryName"
                  />
                </div>
              </div>
              <div class="main-product__details">
                <h1
                  id="template-title"
                  class="main-product__title
                  font-family-freight
                  text-body-xs-regular
                  text-body-l-regular-desktop"
                  v-text="product.title || liquid.title"
                />

                <h2
                  v-if="product?.metafields?.shortDescription"
                  class="main-product__short-description"
                  v-text="product.metafields.shortDescription"
                />

                <rte-content
                  v-if="isGiftCard"
                  class="main-product__description text-body-2xs-regular"
                  :content="product.description || liquid.description || ''"
                />

                <template v-else>
                  <product-prices
                    class="main-product__prices-container"
                    class-compare-at-price="text-body-m-regular"
                    class-price="text-body-m-regular"
                    :available="variant ? variant?.available : true"
                    :compare-at-price="priceObject?.compareAtPrice?.amount"
                    :final-sale="finalSale"
                    :loading="state === 'loading'"
                    :price="priceObject?.price?.amount"
                    :varies="priceObject?.price?.varies"
                  />

                  <product-badge
                    :product="product || liquid"
                    badge-class="main-product__badge top-badge"
                    badge-element="span"
                    badge-type="topBadge"
                  />

                  <div
                    id="yotpo-app"
                    ref="yotpoStarRating"
                    class="main-product__reviews"
                  />

                  <div class="main-product__wishlist">
                    <wishlist-button
                      v-show="renderWishlistBtn"
                      class="product-page__wishlist"
                      :product="product || liquid"
                      template="product"
                      :variant="variant"
                    />
                  </div>
                </template>
              </div>

              <product-form
                :active-merge="activeMerge"
                class="main-product__form"
                :loading="state === 'loading'"
                :product="product || liquid"
                :is-gift-card="isGiftCard"
                :is-shipping-unavailable="isShippingUnavailable"
                :section="section"
                :variant="variant"
                :is-free-gift="isFreeGift"
                source="pdpDetail"
                @add-to-cart="handleAddToCart"
                @update-merge="handleUpdateMerge"
                @update-product-handle="handleUpdateProductHandle"
                @update-variant="handleVariantUpdate"
              />

              <rte-content
                v-if="isGiftCard"
                class="main-product__gift-card-balance text-body-2xs-regular"
                :content="section.giftCard.checkBalance || ''"
              />

              <div
                v-if="!isMobile"
                class="main-product__tabs-wrapper"
              >
                <tabs-snippet
                  :blocks="accordionData"
                  @open-size-guide="openSizeGuide"
                  @open-custom-sg="showSGModal"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="main-product__content-wrapper">
      <div
        :ref="setIntersectionObservers"
        class="main-product__fade-in grid"
      >
        <div
          v-if="isGiftCard"
          class="main-product__gift-card-terms col l6-27"
        >
          <h2
            class="main-product__gift-card-terms-title text-body-2xs-regular"
            v-text="$string('gift_card.terms')"
          />

          <rte-content
            v-if="section.giftCard.terms"
            class="main-product__gift-card-terms-copy text-body-2xs-regular"
            :content="section.giftCard.terms || ''"
          />
        </div>

        <div
          v-if="isMobile"
          class="main-product__accordion-wrapper col xs-span l37-59"
          :style="`--accordion-tablet-padding-top: ${accordionTopPadding}px`"
        >
          <accordion
            heading-element="h2"
            block-class="main-product"
            first-accordion-active
            :accordion-data="accordionData"
            @open-size-guide="openSizeGuide"
          />
          <div
            v-if="section.enableShoppingGives"
            class="main-product__vbgb"
          >
            <shopping-gives-modal
              v-if="section.enableCustomSGModal"
              :content="customVbgbData"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product (main-product)
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Object} section - Section settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import Accordion from '~global/accordion-snippet/accordion-snippet'
import ProductBadge from '~global/product-badge/product-badge'
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductPrices from '~global/product-prices/product-prices'
import ShoppingGivesModal from '~async/shopping-gives-modal/shopping-gives-modal.vue'
import TabsSnippet from '~async/tabs-snippet/tabs-snippet'

import { queryFormatProduct } from '~/helpers/shopify'
import { emitUpdateRequestObject } from '~/helpers/vue'
import { addToCartFromPDP as DYAddToCartFromPDPEvent } from '~/helpers/dynamic-yield'
import { values as breakpoints } from '~/config/breakpoints'

import {
  moveAsyncElement,
  setInitialActiveMerge,
  setIntersectionObserver,
} from '~/helpers/general'

export default {
  name: 'MainProduct',

  components: {
    Accordion,
    Breadcrumbs: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.breadcrumbs' */
        '~async/breadcrumbs/breadcrumbs'
      ),
    }),
    TabsSnippet,
    ProductBadge,
    ProductForm,
    ProductGallery,
    ProductPrices,
    RteContent: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.rte-content' */
        '~async/rte-content/rte-content'
      ),
    }),
    WishlistButton: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.wishlist-button' */
        '~async/wishlist-button/wishlist-button'
      ),
    }),
    ShoppingGivesModal,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    section: {
      type: Object,
      default: () => ({
        displayQuantitySelector: true,
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        dropdownOptions: [],
        excludedInitialSelectOptions: ['size', 'taille', 'denominations'],
        giftCard: {
          checkBalance: '',
          terms: '',
        },
        hideGiftCardOptionNames: true,
        id: '',
        resetFormBehaviour: 'disabled',
        resetRecipientFormOnAdd: true,
        selectDisabledValues: true,
        enableFitPredictor: false,
        enableShoppingGives: true,
        enableCustomSGModal: false,
        customSGAccordionBody: '',
        customSGHeader: '',
        customSGImage: null,
        customSGPoweredBy: null,
        customSGFooter: '',
        productsToShowGalleryVideos: null,
        enableShopPay: true,
      }),
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      activeMerge: {},
      currentIndex: 0,
      currentNamespace: 'pdpGallery',
      handle: this.$variable('page.handle'),
      intersectionObservers: [],
      // Same as setAnimationIntersectionObservers() for consistent animation
      intersectionOptions: {
        rootMargin: '-100px',
        threshold: 0,
      },
      state: 'loading',
      isShippingUnavailable: false,
      isGalleryVisible: true,
      currentSlideIsVideo: false,
      productDetailsObserver: false,
      accordionTopPadding: 0,
      yotpoSectionElement: false,
      metaThemeColor: document.querySelector('meta[name="theme-color"]'),
      mobileHorizontalScroll: true,
      mobileShowGalleryArrows: false,
      horizontalGalleryObserver: false,
      hideGalleryVideo: false,
      hideGalleryVideoObserver: false,
      mobileArrowsObserver: false,
      yotpoTiming: 250,
      updateSplideContainer: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      scroll: (state) => state.index.scroll,
      cart: (state) => state.cart.response,
    }),

    /**
     * Compute if product is a free gift.
     * @returns {Boolean}
     */
    isFreeGift() {
      return this?.cart?.items?.some((item) => {
        // eslint-disable-next-line no-underscore-dangle
        if (item?.properties?._is_fgwp) {
          if (item?.id === this?.variant?.id) {
            return true
          }
        }

        return false
      })
    },

    /**
     * Format accordion data for passing into accordion-snippet child component.
     * @returns {Array}
     */
    accordionData() {
      if (this.isGiftCard) {
        return [
          {
            title: this.$string('gift_card.terms'),
            body: this.section.giftCardTerms,
          },
        ]
      }

      const productData = this.product || this.liquid
      const data = productData?.description
        ? [
          {
            classes: 'main-product__description text-body-2xs-regular',
            title: this.$string('product.accordions.details'),
            body: productData?.description,
            isRteContent: true,
          },
        ]
        : []

      if (productData?.metafields?.fitNotes || productData?.metafields?.fitGraphicScale) {
        data.push({
          title: this.$string('product.accordions.fit_notes'),
          productFitNotes: productData.metafields.fitNotes,
          productFitGraphic: this.fitGraphicData,
        })
      }
      const shippingCopy = this.$localizeRichtext(this.section.shippingAndReturnsCopy)

      let tableHtml = ''
      if (this.section.columnHeader && this.section.tableData) {
        const headerRaw = this.section.columnHeader
        const rowRaw = this.section.tableData

        const headers = headerRaw?.trim().split('|')
        const rows = rowRaw?.trim().split('\n').map(row => row.split('|'))

        if (headers.length && rows.length) {
          tableHtml = `
            <table class="shipping-table">
              <thead>
                <tr>${headers.map(header => `<th>${header.trim()}</th>`).join('')}</tr>
              </thead>
              <tbody>
                ${rows.map(row => `
                  <tr>${row.map(cell => `<td>${cell.trim()}</td>`).join('')}</tr>
                `).join('')}
              </tbody>
            </table>
          `
        }
      }

      data.push({
        title: this.$string('product.accordions.shipping_returns'),
        body: shippingCopy + tableHtml,
      })

      if (this.section.enableShoppingGives) {
        data.push({
          title: this.$string('product.accordions.vb_gives_back'),
          body: this.section.enableCustomSGModal
            ? this.$localizeRichtext(this.section.customSGAccordionBody)
            : '<div class="sg-donation-placeholder"></div>',
          enableCustomSGModal: this.section.enableCustomSGModal
            ? this.customVbgbData
            : false,
        })
      }

      return data
    },

    customVbgbData() {
      return (
        {
          header: this.$localizeRichtext(this.section.customSGHeader),
          image: this.section.customSGImage,
          poweredBy: this.section.customSGPoweredBy,
          footer: this.$localizeRichtext(this.section.customSGFooter),
        }
      )
    },

    /**
     * Format object for passing into breadcrumbs child component.
     * @returns {Object}
     */
    breadcrumbsObject() {
      const cHandle = document.referrer?.split('/collections/')?.[1] || null
      let tier2 = null

      if (cHandle) {
        tier2 = sessionStorage.getItem(`referrer-plp-${cHandle}`)
        tier2 = tier2 ? JSON.parse(tier2) : null
      }

      return {
        tier2: tier2?.url && tier2?.title
          ? tier2
          : this.product.collection || this.liquid.collection,
        tier3: {
          title: this.product.title || this.liquid.title,
          url: this.product.url || this.liquid.url,
        },
      }
    },

    /**
     * Compute final sale.
     * @returns {Boolean}
     */
    finalSale() {
      const tags = this.product?.tags || this.liquid?.tags

      return tags?.some((tag) => {
        return (
          tag.toLowerCase().includes('final sale') ||
          tag.toLowerCase().includes('final_sale') ||
          tag.toLowerCase().includes('finalsale')
        )
      })
    },

    /**
     * The product's fit graphic data.
     * @returns {Object|null} Fit graphic data object
     */
    fitGraphicData() {
      const productData = this.product || this.liquid

      if (!productData?.metafields?.fitGraphicScale) {
        return null
      }

      return {
        overallFit: productData?.metafields?.fitGraphicOverallFit,
        scale: productData?.metafields?.fitGraphicScale,
        low: productData?.metafields?.fitGraphicLow,
        middle: productData?.metafields?.fitGraphicMiddle,
        high: productData?.metafields?.fitGraphicHigh,
      }
    },

    /**
     * Compute the product images' outfit keys.
     * - Computed here to determine if STL component should be rendered.
     * @returns {Array}
     */
    outfitKeys() {
      return this.product?.media
        ?.map((media) => {
          if (!media.raw_alt) {
            return null
          }
          const firstSplit = media.raw_alt.split('{{ outfit:')
          const secondSplit = firstSplit?.[1]?.split(' }}')
          if (!secondSplit?.[1]) {
            return null
          }
          return secondSplit[0].trim()
        })
    },

    /**
     * Computes if product has outfit keys.
     * @returns {Boolean}
     */
    hasOutfitKeys() {
      return this.outfitKeys?.filter(Boolean)?.length ?? false
    },

    /**
     * Compute if has top badge(s).
     * @returns {Boolean}
     */
    hasTopBadges() {
      const productData = this.product || this.liquid

      return Boolean(productData?.metafields?.topBadge) ||
        Boolean(productData?.metafields?.secondaryName)
    },

    /**
     * Whether this is a gift card.
     * @returns {Boolean}
     */
    isGiftCard() {
      const productData = this.product || this.liquid
      return productData.giftCard || productData?.type?.toLowerCase() === 'gift card'
    },

    /**
     * Compute product price object.
     * @returns {Object}
     */
    priceObject() {
      if (this.variant) {
        return this.variant
      }

      return this.product || this.liquid
    },

    /**
     * Compute whether to show mobile breadcrumbs.
     * @returns {Boolean}
     */
    showMobileBreadcrumbs() {
      return !this.isGiftCard &&
        this.isMobile &&
        this.scroll.productBreadcrumbsMobile === 'open'
    },

    /**
     * Compute vendor collection URL.
     * @returns {String}
     */
    vendorCollectionUrl() {
      return this.product.vendor
        ? `${this.$variable('routes.normalisedRoot')}collections/vendors?q=${encodeURI(this.product.vendor)}`
        : ''
    },

    /**
     * Hides wishlist button from CA store
     */
    renderWishlistBtn() {
      return window.cnvs?.store.permanentDomain !== '//veronica-beard-canada.myshopify.com/'
    },

    shopTheLookClasses() {
      return {
        'main-product__gallery-anchor--video': this.currentSlideIsVideo,
      }
    },

    stlGridClass() {
      const formattedKeys = this.outfitKeys.filter(Boolean)
      if (formattedKeys.length >= 4) {
        return 'l-span'
      }
      if (formattedKeys.length >= 3) {
        return 'l1-64'
      }
      return 'l6-59'
    },
  },

  watch: {

    /**
     * Watch handle for update to load new product.
     */
    handle() {
      this.queryProduct(this.handle)
    },

    /**
     * Watch intersection observers to observe or disconnect them.
     * - If new array is empty then disconnect old observers.
     * @param {Array<Object>} newArray - Updated array.
     * @param {Array<Object>} oldArray - Previous array.
     */
    intersectionObservers(newArray, oldArray) {
      if (!newArray.length) {
        this.toggleIntersectionObservers(false, oldArray)
        return
      }

      this.toggleIntersectionObservers(true, newArray)
    },

    /**
     * Update Yotpo reviews on mobile resize.
     */
    isMobile() {
      this.updateProductDetailsObserver()
    },

    /**
     * Set SSP data on variant change.
     */
    variant: {
      async handler(newVariant) {
        if (!this.product || !newVariant) {
          return
        }

        await this.$nextTick()
        this.setProductThemeColor()
        if (this.section.enableFitPredictor) {
          setTimeout(() => {
            this.setSSP()
          }, this.$timing('slow'))
        }
      },
      deep: true,
      immediate: true,
    },
  },

  created() {
    if (cnvs?.settings?.blockedProduct) {
      let geNoticeUpdated = this.lookForGEShippingNotice()

      if (!geNoticeUpdated) {
        const observer = new MutationObserver((mutationList) => {
          for (const mutation of mutationList) {
            if (mutation.type === 'childList') {
              geNoticeUpdated = this.lookForGEShippingNotice()
              if (geNoticeUpdated) {
                observer.disconnect()
                return
              }
            }
          }
        })

        observer.observe(document.querySelector('body'), { childList: true })

        // Give up after two seconds
        setTimeout(() => {
          observer.disconnect()
        }, 6000)
      }
    }

    // Get EN product data needed (pre-order, etc) inaccessible in Liquid
    if (cnvs?.store?.language !== 'en') {
      this.queryProduct(this.handle)
    }
  },

  mounted() {
    this.runYotpoFunctions()

    /**
     * Prevent browser restoring scroll position.
     * - If the product gallery is showing a non-first image then on page
     *   refresh the browser scrolls down the page trying to restore the
     *   position based on the image's scroll position.
     */
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }

    this.setProduct()
    this.setInitialActiveMerge()
    this.setProductDetailsIntersectionObserver()

    this.loadStylesheet('breadcrumbs')
    this.checkProductShowsVideo()
    this.observeMobileArrowsShow()

    setTimeout(() => {
      if (this.section.enableFitPredictor) {
        this.initializeFitPredictor()
      }
    }, this.$timing('slow'))

    this.setYotpoSectionElement()

    this.$nextTick().then(() => {
      const sgLink = document.querySelector('a[title="#vbgb"]')
      if (sgLink) {
        sgLink.addEventListener('click', (event) => {
          event.preventDefault()

          this.showSGModal()
        })
      }
    })

    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
  },

  /**
   * Reset intersection observers before updating.
   */
  beforeUpdate() {
    this.toggleIntersectionObservers(false)
    this.intersectionObservers = []
  },

  /**
   * Toggle intersection observers on update.
   */
  updated() {
    this.toggleIntersectionObservers(true)
  },

  beforeUnmount() {
    if (this.horizontalGalleryObserver) {
      this.horizontalGalleryObserver.disconnect()
    }
  },

  methods: {

    /*
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      loadStylesheet: 'components/loadStylesheet',
      setSizeGuideProduct: 'sizeGuideDrawer/setSizeGuideProduct',
    }),

    /**
     * Open size guide drawer.
     */
    openSizeGuide() {
      this.setSizeGuideProduct(this.product)

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'sizeGuideDrawer',
      })
    },

    /**
     * Watch for GE shipping notice.
     */
    lookForGEShippingNotice() {
      const xpath = '//div[contains(text(),\'This product cannot be shipped to your country\')]'
      const matchingElement = document.evaluate(
        xpath,
        document,
        null,
        XPathResult.FIRST_ORDERED_NODE_TYPE,
        null,
      ).singleNodeValue

      if (matchingElement || (this.product.tags?.includes('international: block') && Shopify.country !== 'US')) {
        this.isShippingUnavailable = true
        return this.updateGEShippingNotice(matchingElement)
      }

      return false
    },

    /**
     * updateGEShippingNotice.
     * @param {HTMLElement} element - The GE shipping notice element
     */
    updateGEShippingNotice(element) {
      if (!element) {
        return false
      }

      element.classList.add('ge-pdp-shipping-notice')
      return true
    },

    /**
     * Set Liquid product object to product.
     */
    setProduct() {
      if (!this.liquid.handle) {
        this.queryProduct()
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false || this.product.handle === handle) {
        return
      }

      this.state = 'loading'

      const selectedSize = this.variant
        ? this.variant.selectedOptions?.find(option => option.name === 'Size' || option.name === 'Taille').value
        : null

      if (this.$variable('settings.disableComponentReadyState')) {
        return
      }

      try {
        const formattedProduct = await queryFormatProduct(handle)

        this.$emit('update-request-object', {
          response: formattedProduct,
          type: 'product',
        })

        if (this.section.enableFitPredictor) {
          await this.$nextTick()

          setTimeout(() => {
            this.initializeFitPredictor(formattedProduct)
          }, this.$timing('slow'))

          if (this.variant === null && formattedProduct.extensions.length !== 0) {
            setTimeout(() => {
              window.ssp('set', 'product', { id: formattedProduct.id, size: '' })
            }, this.$timing('slow'))
          }
        }

        this.updateProductUrl(formattedProduct.handle)
        this.$refs.yotpoStarRating.innerHTML = ''

        if (selectedSize) {
          const selectedSizeVariant = formattedProduct?.variants?.find(variant => {
            return variant.selectedOptions.find(option => option.name === 'Size' || option.name === 'Taille').value === selectedSize
          })

          if (selectedSizeVariant) {
            this.handleVariantUpdate(selectedSizeVariant)
          }
        }

        this.updateYotpoProduct(formattedProduct)
        this.state = 'ready'
        window.cnvs.EventBus.emit('pdp:update-handle', formattedProduct)

      } catch (error) {
        cnvs.ReportError('Failed to load product', error)
      }
    },

    /**
     * Update product URL when loading new handle.
     * - Replaces state so it doesn't affect back navigation.
     * - Use pushState instead to affect back navigation.
     * - Maintains /collections/ URL.
     * @param {String} handle - New product handle.
     */
    async updateProductUrl(handle) {
      const oldHandle = location.pathname.split('/').reverse()[0]

      if (oldHandle === handle) {
        return
      }

      const pathname = location.pathname.replace(oldHandle, handle)
      const url = `${location.origin}${pathname}`

      history.replaceState({
        html: '',
      }, '', url)

      await this.$nextTick()
      this.setProductThemeColor()
    },

    /**
     * Handle add to cart event from product form.
     */
    handleAddToCart() {
      DYAddToCartFromPDPEvent()
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex
      window.cnvs.EventBus.emit('pdp:current-index-updated', newIndex)
    },

    handleUpdateIsVideo(isVideo) {
      this.currentSlideIsVideo = isVideo
    },

    /**
     * Handle merge update.
     * @param {Object} object - Updated object.
     */
    handleUpdateMerge(object) {
      this.activeMerge = object
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace
    },

    /**
     * Handle variant update from product form.
     * @param {Boolean|Null|Object} variant - Emitted variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const params = new URLSearchParams(document.location.search.substring(1))

      if (variant?.id) {
        params.set('variant', variant.id)
      }

      const url = variant?.id
        ? `${location.origin}${location.pathname}?${params.toString()}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Set product theme color.
     */
    setProductThemeColor() {
      if (!this.product?.metafields?.themeColor) {
        document.body.style.removeProperty('--product-theme-color')
        this.metaThemeColor.setAttribute('content', '')
        return
      }

      document.body.style.setProperty('--product-theme-color', this.product.metafields.themeColor)
      this.metaThemeColor.setAttribute('content', this.product.metafields.themeColor)
    },

    /**
     * Set product via SSP API.
     */
    setSSP() {
      if (!window.ssp || !this.variant || !this.product) {
        return
      }

      if (this.variant) {
        const productData = this.product
        const selectedVariant = this.variant
        const swatchOptionName = this.$variable('settings.product.productSwatchOptionNames')

        const swatchOption = selectedVariant?.selectedOptions?.find((option) => {
          return swatchOptionName?.includes(option.name?.toLowerCase())
        })

        const availableSizes = productData?.variants
          ?.filter((sizeVariant) => sizeVariant.available)
          .map((sizeVariant) => {
            const sizeVariantOption = sizeVariant.selectedOptions?.filter((option) => {
              return option.name.toLowerCase() === 'size' || option.name.toLowerCase() === 'taille'
            })

            return sizeVariantOption?.[0]?.value
          })

        window.ssp('set', 'product', {
          productId: productData.id,
          brand: productData.vendor,
          gender: 'female',
          department: productData.type,
          color: swatchOption?.value,
          price: (parseInt(selectedVariant?.price?.amount) / 100).toFixed(2),
          sizes: productData.options.find((opt) => opt.name.toLowerCase() === 'size' || opt.name.toLowerCase() === 'taille')?.values || null,
          size: '',
          availableSizes: [
            ...new Set(
              availableSizes,
            ),
          ],
        })
      }

      this.setSspUser()
      this.setSspProduct()
    },

    /**
     * Initialize Fit Predictor with data available.
     * @param {Object} formattedProduct - Formatted product data.
     */
    initializeFitPredictor(formattedProduct) {
      if (!window.ssp) {
        return
      }

      let layout = 'desktop'
      if (window.innerWidth < breakpoints.l) {
        layout = 'tablet'
      }
      if (window.innerWidth < breakpoints.m) {
        layout = 'mobile'
      }

      // Locale info
      window.ssp('set', 'site', {
        currency: Shopify?.currency?.active || 'USD',
        market: 'US',
        layout,
      })

      // Page type
      window.ssp('set', 'page', {
        type: 'pdp',
      })

      this.setSspUser()
      this.setSspProduct(formattedProduct)

      window.ssp('start', 'fitpredictor')
    },

    /**
     * Update product context.
     * @param {Object} formattedProduct - Formatted product data.
     */
    setSspProduct(formattedProduct) {
      if (!window.ssp || !this.product) {
        return
      }

      const productData = formattedProduct || this.product

      const selectedVariant = this.variant === null
        ? productData.selectedOrFirstAvailableVariant
        : this.variant

      const sizeOption = selectedVariant?.selectedOptions?.find((option) => option.name.toLowerCase() === 'size' || option.name.toLowerCase() === 'taille')
      const swatchOptionName = this.$variable('settings.product.productSwatchOptionNames')

      const swatchOption = selectedVariant?.selectedOptions?.find((option) => {
        return swatchOptionName?.includes(option.name?.toLowerCase())
      })

      const availableSizes = productData?.variants
        ?.filter((sizeVariant) => sizeVariant.available)
        .map((sizeVariant) => {
          const sizeVariantOption = sizeVariant.selectedOptions?.filter((option) => {
            return option.name?.toLowerCase() === 'size' || option.name?.toLowerCase() === 'taille'
          })

          return sizeVariantOption?.[0]?.value
        })

      window.ssp('set', 'product', {
        productId: productData.id,
        brand: productData.vendor,
        gender: 'female',
        department: productData.type,
        color: swatchOption?.value,
        price: (parseInt(selectedVariant?.price?.amount) / 100).toFixed(2),
        sizes: productData.options.find((opt) => opt.name.toLowerCase() === 'size' || opt.name.toLowerCase() === 'taille')?.values || null,
        size: sizeOption?.value ? sizeOption.value : '',
        availableSizes: [
          ...new Set(
            availableSizes,
          ),
        ],
      })
    },

    /**
     * Set Ssp user data if available
     */
    setSspUser() {
      if (window.cnvs.customer.ssp_userid) {
        window.ssp('set', 'user', {
          userId: window.cnvs.customer.ssp_userid,
          email: window.cnvs.customer.ssp_email,
        })
      }
    },

    /**
     * Set intersection observers to fade in elements of main product.
     * @param {HTMLElement} element - Item in v-for.
     */
    setIntersectionObservers(element) {
      const observer = new IntersectionObserver(
        this.handleIntersection,
        this.intersectionOptions,
      )

      this.intersectionObservers.push({
        element,
        observer,
      })
    },

    /**
     * Toggle intersection observers.
     * @param {Boolean} enable - Enable observers, false disconnects them.
     * @param {Array<Object>} [intersectionObservers] - Observers to toggle.
     */
    toggleIntersectionObservers(enable, intersectionObservers = this.intersectionObservers) {
      intersectionObservers.forEach(({ element, observer }) => {
        if (enable && element) {
          observer.observe(element)
          return
        }

        observer.disconnect()
      })
    },

    /**
     * Handle intersection.
     * @param {Array<Object>} intersections - Intersection array.
     * @param {Object} observer - Intersection observer.
     */
    handleIntersection(intersections, observer) {
      const intersection = intersections[0]

      if (
        !intersection ||
        !intersection.isIntersecting
      ) {
        return
      }

      intersection.target?.classList.add(this.$class('active'))
      observer.unobserve(intersection.target)
    },

    /**
     * Set initial active merge.
     * @param {Object} [product] - Query product data.
     */
    setInitialActiveMerge(product) {
      const productData = product || this.product || this.liquid
      const activeMerge = setInitialActiveMerge(productData)

      if (!activeMerge) {
        return
      }

      this.activeMerge = activeMerge
    },

    /**
     * Handle update product handle.
     * @param {Object} handle - Product handle to update.
     */
    handleUpdateProductHandle(handle) {
      this.handle = handle
    },

    /**
     * Hide gallery when product details drag button is no longer in view.
     * This will prevent the gallery from being visible through scroll bounce
     */
    setProductDetailsIntersectionObserver() {
      const element = this.$refs.productDetailsDrag

      if (!element || !this.isMobile) {
        return
      }

      this.productDetailsObserver = setIntersectionObserver({
        callback: (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.isGalleryVisible = true
              return
            }

            this.isGalleryVisible = false
          })

        },
        element,
        threshold: 0,
      })
    },

    /**
     * Disconnect or initialize product details intersection observer
     */
    updateProductDetailsObserver() {
      if (!this.isMobile && this.productDetailsObserver) {
        this.isGalleryVisible = true
        this.productDetailsObserver.disconnect()
        this.productDetailsObserver = false
      } else if (this.isMobile && !this.productDetailsObserver) {
        this.setProductDetailsIntersectionObserver()
      }
    },

    /**
     * Calculate sticky sidebar height relative to the screen height
     * and add padding to the accordion wrapper
     */
    calculateFitsPadding() {
      if (this.isMobile || window.screen.height > 800) {
        return
      }

      setTimeout(() => {
        const stickySideBarHeight = this.$refs.stickyDetails.scrollHeight
        const screenHeight = window.screen.height
        const sectionOverflowSize = stickySideBarHeight - screenHeight

        if (sectionOverflowSize > -50) {
          this.accordionTopPadding = Math.abs(sectionOverflowSize) + 70
        }
      }, this.$timing('skeleton'))
    },

    /**
     * Debug messaging.
     * - Outputs console log on non-published themes.
     */
    debugMessage(message, value) {
      if (this.$variable('theme.published')) {
        return
      }

      if (value) {
        window.console.debug('%c Debug ', 'background: DarkSlateBlue; color: white', message, value)
        return
      }

      window.console.debug('%c Debug ', 'background: DarkSlateBlue; color: white', message)
    },

    /**
     * Run Yotpo functions.
     */
    async runYotpoFunctions() {
      this.debugMessage('START Yotpo functions')

      this.setYotpoMutationObserver()
      this.setYotpoIntersectionObserver()
      await this.refreshYotpoWidgets()
      this.moveYotpoStarRating()

      this.debugMessage('END Yotpo functions')
    },

    /**
     * Set mutation observer on Yotpo section.
     * - Adds relevant classes after Yotpo injection.
     */
    setYotpoMutationObserver() {
      this.handleYotpoMutation()

      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type !== 'childList' || !mutation.addedNodes?.length) {
            continue
          }

          for (const addedNode of mutation.addedNodes) {
            if (addedNode.id !== 'yotpo-reviews-main-widget') {
              continue
            }

            this.handleYotpoMutation()
            return
          }
        }
      })

      observer.observe(document.body, { childList: true, subtree: true })
    },

    /**
     * Handle Yotpo mutation.
     */
    handleYotpoMutation() {
      const yotpoParent = [...document.querySelectorAll('.yotpo-widget-instance')]
      const yotpoChild = [...document.querySelectorAll('.yotpo-reviews-main-widget')]

      yotpoParent.forEach((component) => component.classList.add('grid'))
      yotpoChild.forEach((child) => child.classList.add('col', 'xs-span', 'l6-59'))
      this.debugMessage('Yotpo mutation')
    },

    /**
     * Set interaction observer on the Yotpo section.
     * - Adds class on intersection.
     */
    setYotpoIntersectionObserver() {
      const intersectionInterval = setInterval(() => {
        const element = document.querySelector('.yotpo-widget-instance')

        if (!element) {
          this.debugMessage('Set Yotpo intersection', false)
          return
        }

        clearInterval(intersectionInterval)
        this.debugMessage('Set Yotpo intersection', true)

        setIntersectionObserver({
          callback: this.handleYotpoIntersection,
          element,
          rootMargin: '-100px',
          threshold: 0,
        })
      }, this.yotpoTiming)
    },

    /**
     * Handle Yotpo intersection.
     * @param {Array} entries - Intersection entries.
     * @param {Object} observer - Intersection observer.
     */
    handleYotpoIntersection(entries, observer) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        this.debugMessage('Yotpo intersection')
        entry.target.classList.add(this.$class('active'))
        observer.unobserve(entry.target)
      })
    },

    /**
     * Refresh Yotpo widget when available.
     * - Solution from Yotpo, attempts to remove the 'Marked with ...' text.
     * @returns {Promise<Object>}
     */
    refreshYotpoWidgets() {
      return new Promise((resolve) => {
        const refreshInterval = setInterval(async() => {
          if (typeof yotpoWidgetsContainer === 'undefined') {
            this.debugMessage('Refresh Yotpo widgets', false)
            return
          }

          clearInterval(refreshInterval)
          this.debugMessage('Refresh Yotpo widgets', true)
          yotpoWidgetsContainer.initWidgets()
          await this.$nextTick()
          resolve()
        }, this.yotpoTiming)
      })
    },

    /**
     * Move Yotpo star rating.
     * - Moves star rating widget from Yotpo section to desired location.
     */
    moveYotpoStarRating() {
      moveAsyncElement(
        '.yotpo-reviews-star-ratings-widget',
        this.$refs.yotpoStarRating,
        this.updateYotpoRatingWidget,
      )
    },

    /**
     * Update Yotpo rating widget text.
     */
    updateYotpoRatingWidget(element) {
      this.debugMessage('Moved Yotpo star rating')
      element.querySelector('.yotpo-sr-bottom-line-text').innerText =
        element.querySelector('.yotpo-sr-bottom-line-text')
          .innerText
          .replace(/reviews?/gi, '')
    },

    /**
     * Set Yotpo section element.
     * - Can't create separate section to handle this so find section which has
     *   block elements inside it with ID containing 'yotpo'.
     */
    setYotpoSectionElement() {
      const sectionInterval = setInterval(() => {
        const sections = [...document.querySelectorAll('.shopify-section')]

        const yotpoSectionElement = sections?.find((element) => {
          return [...element.children]?.some((child) => {
            return child.id?.includes('yotpo')
          })
        })

        if (!yotpoSectionElement) {
          return
        }

        this.yotpoSectionElement = yotpoSectionElement
        clearInterval(sectionInterval)
      }, this.yotpoTiming)
    },

    /**
     * Update Yotpo section based on new product.
     * - Uses section rendering API.
     * @param {Object} product - New product.
     */
    async updateYotpoProduct(product) {
      try {
        if (!this.yotpoSectionElement) {
          return
        }

        const id = this.yotpoSectionElement.id?.replace('shopify-section-', '')
        const url = `${this.$variable('routes.normalisedRoot')}products/${product.handle}?sections=${id}`
        let response = await fetch(url)
        response = await response.json()

        if (!response[id]) {
          return
        }

        this.yotpoSectionElement.innerHTML = response[id]
        this.runYotpoFunctions()

      } catch (error) {
        cnvs.ReportError(error)
      }
    },

    /**
     * Checks for DY element in the DOM and triggers horizontal scrolling
     */
    observeHorizontalGalleryTrigger() {
      const observerConfig = {
        attributes: true,
        childList: true,
        characterData: true,
      }

      this.horizontalGalleryObserver = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type !== 'childList' || !mutation.addedNodes.length) {
            continue
          }

          const sizeElement = document.querySelector('.dy-gallery-horizontal-scroll')

          if (sizeElement) {
            this.mobileHorizontalScroll = true

            this.horizontalGalleryObserver.disconnect()
            return
          }
        }
      })

      this.horizontalGalleryObserver.observe(document.body, observerConfig)
    },

    /**
     * Check product in list of products to for showing video in gallery
     */
    checkProductShowsVideo() {
      const product = this.liquid || this.product
      const productWithVideo = this.section.productsToShowGalleryVideos.includes(product.id)

      if (productWithVideo) {
        this.hideGalleryVideo = true

        this.observeGalleryVideoShow()
      }
    },

    /**
     * Show Shopping Gives Modal
     */
    showSGModal() {
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'ShoppingGivesModal',
      })
    },

    /**
     * Checks for DY element in the DOM and triggers showing video in gallery
     */
    observeGalleryVideoShow() {
      const observerConfig = {
        attributes: true,
        childList: true,
        characterData: true,
      }

      this.hideGalleryVideoObserver = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type !== 'childList' || !mutation.addedNodes.length) {
            continue
          }

          const triggerElement = document.querySelector('.dy-show-gallery-video')

          if (triggerElement) {
            this.hideGalleryVideo = false

            this.hideGalleryVideoObserver.disconnect()
            return
          }
        }
      })

      this.hideGalleryVideoObserver.observe(document.body, observerConfig)
    },

    checkScreenSize() {
      // if (window.innerWidth < 1400 && window.innerWidth > 1024) {
      //   this.updateSplideContainer = true
      // } else {
      //   this.updateSplideContainer = false
      // }
    },

    /**
     * Checks for DY element in the DOM and triggers
     * showing mobile gallery arrows
     */
    observeMobileArrowsShow() {
      const observerConfig = {
        attributes: true,
        childList: true,
      }

      this.mobileArrowsObserver = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type !== 'childList' || !mutation.addedNodes.length) {
            continue
          }

          const triggerElement = document.querySelector('.dy-gallery-mobile-arrows')

          if (triggerElement) {
            this.mobileShowGalleryArrows = true

            this.mobileArrowsObserver.disconnect()
            return
          }
        }
      })

      this.mobileArrowsObserver.observe(document.body, observerConfig)
    },
  },
}
</script>

<style lang="scss">
@import './main-product';
</style>
